import { useState } from 'react'
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock } from 'react-icons/fa'
import SEO from '../../lib/SEO'
import { sendContactRequest } from '../../api'
import styles from './contact.module.scss'

const Contact = () => {
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    origin: '',
    destination: '',
    type: '',
    message: '',
  })
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('sending')
    try {
      const subject = `Quote Request – ${form.name}${form.type ? ` | ${form.type}` : ''}${form.origin && form.destination ? ` | ${form.origin} → ${form.destination}` : ''}`
      const message = [
        form.company && `Company: ${form.company}`,
        form.origin && `Origin: ${form.origin}`,
        form.destination && `Destination: ${form.destination}`,
        form.type && `Service: ${form.type}`,
        form.message && `\n${form.message}`,
      ].filter(Boolean).join('\n')

      await sendContactRequest('vision', {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        subject,
        message,
      })
      setStatus('success')
      setForm({ name: '', company: '', email: '', phone: '', origin: '', destination: '', type: '', message: '' })
    } catch {
      setStatus('error')
    }
  }

  return (
    <>
      <SEO
        title='Contact Vision International – Get a Free Freight Quote'
        description='Contact Vision International for a free freight quote. Call +1 (630) 504-2688 or fill out our form. 24/7 dispatch, nationwide coverage.'
        keywords='Vision International contact, freight quote, trucking quote, Chicago carrier, shipping quote'
        canonical='https://vi4u.net/contact-us'
      />

      <div className={styles.page}>
        <div className={styles.hero}>
          <span className={styles.label}>Get a Quote</span>
          <h1>
            Let's Move
            <br />
            <span className={styles.yellow}>Your Freight.</span>
          </h1>
          <p>Fill out the form — we respond within 30 minutes. Dispatch available 24/7.</p>
        </div>

        <div className={styles.layout}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.row}>
              <div className={styles.field}>
                <label>Your Name *</label>
                <input type='text' name='name' required placeholder='John Smith' value={form.name} onChange={handleChange} />
              </div>
              <div className={styles.field}>
                <label>Company</label>
                <input type='text' name='company' placeholder='Acme Corp' value={form.company} onChange={handleChange} />
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.field}>
                <label>Email *</label>
                <input type='email' name='email' required placeholder='john@company.com' value={form.email} onChange={handleChange} />
              </div>
              <div className={styles.field}>
                <label>Phone</label>
                <input type='tel' name='phone' placeholder='+1 (555) 000-0000' value={form.phone} onChange={handleChange} />
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.field}>
                <label>Origin (City, State) *</label>
                <input type='text' name='origin' required placeholder='Chicago, IL' value={form.origin} onChange={handleChange} />
              </div>
              <div className={styles.field}>
                <label>Destination (City, State) *</label>
                <input type='text' name='destination' required placeholder='Dallas, TX' value={form.destination} onChange={handleChange} />
              </div>
            </div>
            <div className={styles.field}>
              <label>Service Type</label>
              <select name='type' value={form.type} onChange={handleChange}>
                <option value=''>Select service...</option>
                <option value='FTL'>Full Truckload (FTL)</option>
                <option value='LTL'>Less-Than-Truckload (LTL)</option>
                <option value='Expedited'>Expedited Freight</option>
                <option value='Refrigerated'>Refrigerated / Reefer</option>
                <option value='Flatbed'>Flatbed / Specialized</option>
              </select>
            </div>
            <div className={styles.field}>
              <label>Details</label>
              <textarea name='message' rows={4} placeholder='Cargo type, weight, dimensions, pickup date, special requirements...' value={form.message} onChange={handleChange} />
            </div>

            {status === 'success' && (
              <div className={styles.successMsg}>
                Message sent! We'll get back to you within 30 minutes.
              </div>
            )}
            {status === 'error' && (
              <div className={styles.errorMsg}>
                Something went wrong. Please try again or call us directly.
              </div>
            )}

            <button type='submit' className={styles.submit} disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending...' : 'Send Quote Request'}
            </button>
          </form>

          <div className={styles.info}>
            <div className={styles.infoCard}>
              <h2>Contact</h2>
              {[
                { icon: <FaMapMarkerAlt />, label: 'Headquarters', val: '1005 N Commons\nAurora, IL 60504' },
                { icon: <FaPhoneAlt />, label: 'Dispatch (24/7)', val: '+1 (630) 504-2688', href: 'tel:+16305042688' },
                { icon: <FaEnvelope />, label: 'Email', val: 'dispatch@vi4u.net', href: 'mailto:dispatch@vi4u.net' },
                { icon: <FaClock />, label: 'Hours', val: '24 hrs · 7 days · 365 days' },
              ].map((item) => (
                <div key={item.label} className={styles.infoItem}>
                  <div className={styles.infoIcon}>{item.icon}</div>
                  <div>
                    <strong>{item.label}</strong>
                    {item.href ?
                      <a href={item.href}>{item.val}</a>
                    : <p style={{ whiteSpace: 'pre-line' }}>{item.val}</p>}
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.dotCard}>
              <span>DOT # 1312675</span>
              <span>MC# 550806</span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Contact
